import React, { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";

import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";

import { DRAWER_WIDTH } from "theme/muiTheme";

const navLinkStyle = { textDecoration: "none", color: "inherit", display: "block" };

function DrawerContent({ routes }) {
  const location = useLocation();
  const [openMenus, setOpenMenus] = useState({});
  const [versionFecha, setVersionFecha] = useState(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/backup-fecha-control`, {
      headers: { Authorization: localStorage.getItem("token") },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.fecha) {
          const [y, m, d] = data.fecha.split("T")[0].split("-");
          setVersionFecha(`${d}/${m}/${y}`);
        }
      })
      .catch(() => {});
  }, []);

  const isActive = (route) =>
    location.pathname === route.layout + route.path;

  const toggleMenu = (key) =>
    setOpenMenus((prev) => ({ ...prev, [key]: !prev[key] }));

  const adminRoutes = routes.filter((r) => r.tipo !== "DFMENU");

  const renderRoutes = (routeList, depth = 0) =>
    routeList.map((route, i) => {
      if (route.category && route.views) {
        const isOpen = !!openMenus[route.state];
        return (
          <React.Fragment key={route.key || i}>
            <ListItemButton
              onClick={() => toggleMenu(route.state)}
              sx={{ pl: 2 + depth * 1.5, py: 1 }}
            >
              <ListItemText
                primary={
                  <Typography variant="body2" fontWeight="bold" color="text.secondary">
                    {route.name}
                  </Typography>
                }
              />
              {isOpen ? (
                <ExpandLess fontSize="small" sx={{ color: "text.secondary" }} />
              ) : (
                <ExpandMore fontSize="small" sx={{ color: "text.secondary" }} />
              )}
            </ListItemButton>
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <List disablePadding>
                {renderRoutes(route.views, depth + 1)}
              </List>
            </Collapse>
          </React.Fragment>
        );
      }

      if (route.layout !== "/admin") return null;

      const active = isActive(route);
      return (
        <NavLink
          to={route.layout + route.path}
          key={route.key || i}
          style={navLinkStyle}
        >
          <Tooltip title={route.name} placement="right" disableHoverListener>
            <ListItemButton
              selected={active}
              sx={{ pl: 2 + depth * 1.5, py: 1, mx: 1, mb: 0.5 }}
            >
              {route.icon && (
                <ListItemIcon
                  sx={{ color: active ? "white" : "primary.main", minWidth: 36 }}
                >
                  {route.icon}
                </ListItemIcon>
              )}
              <ListItemText
                primary={
                  <Typography
                    variant="body2"
                    fontWeight={active ? "bold" : "normal"}
                    sx={{ color: active ? "white" : "text.primary" }}
                  >
                    {route.name}
                  </Typography>
                }
              />
            </ListItemButton>
          </Tooltip>
        </NavLink>
      );
    });

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Box sx={{ p: 2, pt: 3 }}>
        <Box
          sx={{
            textAlign: "center",
            py: 1.5,
            px: 1,
            borderRadius: 2,
            bgcolor: "#f0fff4",
            border: "1px solid",
            borderColor: "#c6f6d5",
            mb: 1,
          }}
        >
          <Typography variant="caption" display="block" fontWeight="bold" color="success.dark">
            Versión de los datos:
          </Typography>
          <Typography variant="caption" display="block" color="success.main" fontWeight="bold">
            {versionFecha ?? "Cargando..."}
          </Typography>
          <Typography
            variant="caption"
            display="block"
            color="success.dark"
            sx={{ fontStyle: "italic", fontSize: "0.65rem" }}
          >
            Área de Sistemas de CALF
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ mx: 2 }} />

      <Box sx={{ flexGrow: 1, overflowY: "auto", overflowX: "hidden", py: 1 }}>
        <List dense disablePadding>
          {renderRoutes(adminRoutes)}
        </List>
      </Box>

      <Divider sx={{ mx: 2 }} />
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography variant="caption" color="text.secondary">
          © {new Date().getFullYear()} Genova
        </Typography>
      </Box>
    </Box>
  );
}

function AppSidebar({ routes, mobileOpen, onClose }) {
  return (
    <Box
      component="nav"
      sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
      aria-label="navegación principal"
    >
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { width: DRAWER_WIDTH, borderRadius: "0 20px 20px 0" },
        }}
      >
        <DrawerContent routes={routes} />
      </Drawer>

      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            position: "fixed",
            top: 0,
            height: "100vh",
            borderRadius: "0 20px 20px 0",
          },
        }}
        open
      >
        <DrawerContent routes={routes} />
      </Drawer>
    </Box>
  );
}

export default AppSidebar;

export function SidebarResponsive() {
  return null;
}
